import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Briefcase, Megaphone, GraduationCap, Sparkles, Smile, Heart, Gem, Zap, Globe, Film, Laugh, MinusSquare, Clock, BookOpen, Rewind, ChevronDown, ChevronUp, Home, Tag, TrendingDown, PartyPopper, Calendar } from "lucide-react";

// Icon mapping for content styles
const styleIcons = {
  professional: Briefcase,
  promotional: Megaphone,
  educational: GraduationCap,
  inspirational: Sparkles,
  casual: Smile,
  romantic: Heart,
  luxury: Gem,
  energetic: Zap,
  cultural: Globe,
  documentary: Film,
  humorous: Laugh,
  minimalist: MinusSquare,
  urgent: Clock,
  storytelling: BookOpen,
  nostalgic: Rewind,
};

// Icon mapping for content types
const typeIcons = {
  open_house: Home,
  just_listed: Tag,
  price_reduced: TrendingDown,
  sold: PartyPopper,
  coming_soon: Calendar,
  market_update: Briefcase,
  new_dish: Sparkles,
  special_offer: Tag,
  chef_special: Sparkles,
  behind_scenes: Film,
  workout_tips: Zap,
  transformation: Zap,
  class_schedule: Calendar,
  motivation: Sparkles,
  new_arrival: Tag,
  special_deal: Tag,
  test_drive: Zap,
  destination: Globe,
  travel_tips: Sparkles,
  package_deal: Tag,
  new_collection: Sparkles,
  style_guide: Sparkles,
  sale: Tag,
  announcement: Megaphone,
  showcase: Sparkles,
  promotion: Tag,
  testimonial: Sparkles,
};

export const ContentStyleSelector = ({ 
  selected, 
  onChange, 
  options = {},
  suggestedStyles = [],
  // New props for category-adaptive behavior
  category = null,
  contentType = null,
  onContentTypeChange = null,
  contentTypes = {},
  listingDetails = {},
  onAutoFillTemplate = null
}) => {
  const [showAllStyles, setShowAllStyles] = useState(false);
  const styleEntries = Object.entries(options);
  
  // Separate suggested and other styles
  const suggestedStyleEntries = styleEntries.filter(([id]) => suggestedStyles.includes(id));
  const otherStyleEntries = styleEntries.filter(([id]) => !suggestedStyles.includes(id));
  
  // Content types for the category
  const typeEntries = Object.entries(contentTypes);
  
  if (styleEntries.length === 0) {
    return null;
  }
  
  // Function to handle content type selection and trigger auto-fill
  const handleContentTypeSelect = (typeId) => {
    if (onContentTypeChange) {
      onContentTypeChange(typeId);
    }
    
    // If there are listing details and this type has suggested fields, trigger auto-fill
    const typeInfo = contentTypes[typeId];
    if (typeInfo && typeInfo.template_match && onAutoFillTemplate && Object.keys(listingDetails).length > 0) {
      onAutoFillTemplate(typeInfo.template_match, listingDetails);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Content Types (Category-Specific) - Show first if available */}
      {typeEntries.length > 0 && (
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-viraly-light flex items-center gap-2">
              <Tag className="w-5 h-5 text-purple-400" />
              What type of post is this?
              <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full ml-2">Category: {category || 'General'}</span>
            </h3>
            <p className="text-sm text-viraly-muted mt-1">
              Choose based on what you're sharing - this helps us suggest the right template
            </p>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {typeEntries.map(([typeId, typeInfo]) => {
              const isSelected = contentType === typeId;
              const Icon = typeIcons[typeId] || Tag;
              
              return (
                <motion.button
                  key={typeId}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleContentTypeSelect(typeId)}
                  className={`relative p-3 rounded-xl border-2 text-left transition-all ${
                    isSelected 
                      ? "border-purple-500 bg-purple-500/10" 
                      : "border-white/10 bg-white/5 hover:border-purple-500/50"
                  }`}
                  data-testid={`content-type-${typeId}`}
                >
                  {isSelected && (
                    <div className="absolute top-2 right-2 w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center">
                      <Check className="w-2.5 h-2.5 text-white" />
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{typeInfo.emoji}</span>
                    <Icon className={`w-3.5 h-3.5 ${isSelected ? 'text-purple-400' : 'text-viraly-muted'}`} />
                  </div>
                  
                  <h4 className={`font-medium text-sm ${isSelected ? 'text-purple-400' : 'text-viraly-light'}`}>
                    {typeInfo.name}
                  </h4>
                  
                  <p className="text-[10px] text-viraly-muted mt-1 line-clamp-2">
                    {typeInfo.description}
                  </p>
                  
                  {/* Show template match indicator */}
                  {typeInfo.template_match && (
                    <div className="mt-2 text-[9px] text-emerald-400/70 flex items-center gap-1">
                      <Check className="w-2.5 h-2.5" />
                      Template available
                    </div>
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Content Styles (Tone/Mood) */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-viraly-light flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-viraly-orange" />
              Choose Your Content Style
              <span className="text-xs bg-viraly-orange/20 text-viraly-orange px-2 py-0.5 rounded-full ml-2">Required</span>
            </h3>
            <p className="text-sm text-viraly-muted mt-1">
              This shapes your voiceover tone, music mood, captions, and visual style
            </p>
          </div>
        </div>
        
        {/* Suggested Styles First */}
        {suggestedStyleEntries.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-viraly-muted/80 flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-viraly-green" />
              Suggested for your content
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {suggestedStyleEntries.map(([styleId, style]) => (
                <StyleButton 
                  key={styleId}
                  styleId={styleId}
                  style={style}
                  isSelected={selected === styleId}
                  isSuggested={true}
                  onClick={() => onChange(styleId)}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* Other Styles (Collapsible) */}
        {otherStyleEntries.length > 0 && (
          <div className="space-y-2">
            <button
              onClick={() => setShowAllStyles(!showAllStyles)}
              className="text-xs text-viraly-muted/80 flex items-center gap-1 hover:text-viraly-light transition-colors"
            >
              {showAllStyles ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {showAllStyles ? 'Show less' : `Show ${otherStyleEntries.length} more styles`}
            </button>
            
            <AnimatePresence>
              {showAllStyles && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3"
                >
                  {otherStyleEntries.map(([styleId, style]) => (
                    <StyleButton 
                      key={styleId}
                      styleId={styleId}
                      style={style}
                      isSelected={selected === styleId}
                      isSuggested={false}
                      onClick={() => onChange(styleId)}
                    />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
        
        {/* Tip text */}
        <p className="text-xs text-viraly-muted/60 italic">
          Pro tip: Your brand category auto-suggests styles, but you're in control for each post.
        </p>
      </div>
    </div>
  );
};

// Extracted StyleButton component for cleaner code
const StyleButton = ({ styleId, style, isSelected, isSuggested, onClick }) => {
  const Icon = styleIcons[styleId] || Sparkles;
  
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`relative p-4 rounded-xl border-2 text-left transition-all ${
        isSelected 
          ? "border-viraly-orange bg-viraly-orange/10" 
          : "border-white/10 bg-white/5 hover:border-viraly-orange/50"
      }`}
      data-testid={`content-style-${styleId}`}
    >
      {/* Suggested badge */}
      {isSuggested && !isSelected && (
        <div className="absolute -top-2 -right-2 bg-viraly-green text-white text-[10px] px-1.5 py-0.5 rounded-full">
          Suggested
        </div>
      )}
      
      {/* Selected checkmark */}
      {isSelected && (
        <div className="absolute top-2 right-2 w-5 h-5 bg-viraly-orange rounded-full flex items-center justify-center">
          <Check className="w-3 h-3 text-white" />
        </div>
      )}
      
      {/* Icon and emoji */}
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">{style.emoji}</span>
        <Icon className={`w-4 h-4 ${isSelected ? 'text-viraly-orange' : 'text-viraly-muted'}`} />
      </div>
      
      {/* Name */}
      <h4 className={`font-medium text-sm ${isSelected ? 'text-viraly-orange' : 'text-viraly-light'}`}>
        {style.name}
      </h4>
      
      {/* Best for */}
      <p className="text-[10px] text-viraly-muted mt-1">
        Best for: {style.best_for?.slice(0, 2).join(", ")}
      </p>
      
      {/* Description */}
      <p className="text-[10px] text-viraly-muted/70 mt-1 line-clamp-2">
        {style.description}
      </p>
    </motion.button>
  );
};
